import { TextWithMentions } from "@/components/ui";
import { CreatedByUserText } from "@/components/ui/CreatedByUserText/CreatedByUserText";
import { MatterBreadcrumb } from "@/components/ui/MatterBreadcrumb.tsx/MatterBreadcrumb";
import { useGetSubitemsForBreadcrumbs } from "@/hooks/use-get-subitems-for-breadcrumbs";
import { getUsersOptions } from "@/lib/data/react-query";
import type { MatterEvent } from "@/lib/types";
import {
  formatDiffFieldLabel,
  formatDiffValue,
  isHiddenEventLogChangeField,
} from "@/lib/utils/eventLogDiffFormat";
import {
  getEventLogDiff,
  parseEventLogData,
  type EventLogDiff,
} from "@/lib/utils/eventUtils";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState, type ReactNode } from "react";

export const EventLogCard = React.memo(
  ({
    event: sourceEvent,
    actor,
    action,
    prefix,
  }: {
    event: MatterEvent;
    matter?: { id?: string | null };
    actor?: {
      firstName?: string | null;
      id: string;
      lastName?: string | null;
    } | null;
    action?: ReactNode;
    prefix?: ReactNode;
  }) => {
    const [showFieldChanges, setShowFieldChanges] = useState(false);
    const event = useMemo(
      () => normalizeMatterEvent(sourceEvent),
      [sourceEvent],
    );

    const { currentPayload, previousPayload } = useMemo(() => {
      return {
        currentPayload: parseEventLogData(event.matterEventLogDataCurrent),
        previousPayload: parseEventLogData(
          event.matterEventLogDataBeforeAction,
        ),
      };
    }, [event]);

    const isCreateOrDeleteEvent = useMemo(() => {
      const hasCurrent = !!currentPayload && Object.keys(currentPayload).length;
      const hasPrevious =
        !!previousPayload && Object.keys(previousPayload).length;
      if (!hasCurrent && !hasPrevious) {
        return true;
      }
      return (hasCurrent && !hasPrevious) || (!hasCurrent && hasPrevious);
    }, [currentPayload, previousPayload]);

    const isCreateEvent = useMemo(() => {
      const actionText = `${event.actionType ?? ""} ${event.matterEventLogMessage}`;
      return /\bcreat(?:e|ed)\b/i.test(actionText);
    }, [event.actionType, event.matterEventLogMessage]);

    const eventFieldChanges = useMemo<EventLogDiff[]>(() => {
      if (isCreateOrDeleteEvent && !isCreateEvent) {
        return [];
      }
      return getEventLogDiff(event).filter(
        (change) => !isHiddenEventLogChangeField(change.field),
      );
    }, [event, isCreateEvent, isCreateOrDeleteEvent]);
    const eventMessageIncludesUserConnection = event.matterEventLogMessage
      .toLowerCase()
      .includes("user connection");
    const isUserConnectionChange = useMemo(
      () =>
        eventFieldChanges.some((change) =>
          change.field.includes("matterUserConnection"),
        ) || eventMessageIncludesUserConnection,
      [eventFieldChanges, eventMessageIncludesUserConnection],
    );
    const { data: users } = useQuery(getUsersOptions());
    const getUserForUserId = (id: string) => {
      const user = users?.data.find((user) => user.id === id);
      const userName = user
        ? `${user.firstName} ${user.lastName}`
        : "Unknown User";
      return { userName, user };
    };
    const getSubitemsForBreadcrumbs = useGetSubitemsForBreadcrumbs();
    const breadcrumbItems = [
      {
        id: "matter",
        type: "Matter" as const,
      },
      ...getSubitemsForBreadcrumbs(sourceEvent),
    ];

    useEffect(() => {
      setShowFieldChanges(false);
    }, [event.matterEventLogId]);

    return (
      <div className="bg-card border-border/50 flex items-start space-x-3 rounded-lg border p-3">
        <div className="pt-1">{prefix}</div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="text-foreground font-medium">
              <MatterBreadcrumb items={breadcrumbItems} />
            </div>
            {action}
          </div>
          <p className="text-muted-foreground mt-1 line-clamp-4 text-sm">
            <TextWithMentions
              text={event.matterEventLogMessage}
              users={users?.data ?? []}
            />
          </p>
          {!isUserConnectionChange && eventFieldChanges.length > 0 && (
            <div className="mt-2 w-full">
              {!showFieldChanges ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowFieldChanges(true);
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                  className="text-primary text-sm font-semibold underline-offset-2 hover:underline focus-visible:underline"
                >
                  View changes
                </button>
              ) : (
                <div
                  className="border-border/40 bg-muted/40 rounded-lg border px-3 py-2"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
                    Changes
                  </p>
                  <div className="mt-2 space-y-2">
                    {eventFieldChanges.map((change, index) => {
                      return (
                        <div
                          key={`${change.field}-${index}`}
                          className="text-muted-foreground space-y-1 text-xs"
                        >
                          <p className="text-foreground font-semibold">
                            {formatDiffFieldLabel(change.field)}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 break-words">
                            {!isCreateEvent && (
                              <>
                                <span className="border-destructive/30 bg-destructive/10 text-destructive max-w-full rounded-full border px-2 py-0.5 font-medium break-words">
                                  {formatDiffValue(change.before)}
                                </span>
                                <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
                                  →
                                </span>
                              </>
                            )}
                            <span className="max-w-full rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium break-words text-emerald-700">
                              {formatDiffValue(change.after)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <CreatedByUserText
            user={
              actor
                ? {
                    id: actor.id,
                    firstName: actor.firstName ?? "",
                    lastName: actor.lastName ?? "",
                  }
                : getUserForUserId(event.matterEventLogUsername).user
            }
            dateTime={event.dateCreated}
            showTime
          />
        </div>
      </div>
    );
  },
);

EventLogCard.displayName = "EventLogCard";

type LegacyEventSubject = {
  incidentReportId?: string;
  lienId?: string;
  matterEmailId?: string;
  matterExpenseId?: string;
  matterFileId?: string;
  matterInsuranceId?: string;
  matterLitigationDateId?: string;
  matterLostWagesId?: string;
  matterNoteId?: string;
  matterPropertyDamageId?: string;
  matterSettlementCounterId?: string;
  matterSettlementDemandId?: string;
  matterSettlementOfferId?: string;
  matterTrustCheckId?: string;
  matterVehicleId?: string;
  medicalTreatmentAppointmentId?: string;
  medicalTreatmentBalanceConfirmationId?: string;
  medicalTreatmentBillId?: string;
  medicalTreatmentId?: string;
  medicalTreatmentPlanId?: string;
  medicalTreatmentRecordId?: string;
  taskId?: string;
};

const eventSubjectField: Record<string, keyof LegacyEventSubject> = {
  incident_report: "incidentReportId",
  matter_email: "matterEmailId",
  matter_expense: "matterExpenseId",
  matter_file: "matterFileId",
  matter_insurance: "matterInsuranceId",
  matter_lien: "lienId",
  matter_litigation_date: "matterLitigationDateId",
  matter_lost_wages: "matterLostWagesId",
  matter_note: "matterNoteId",
  matter_property_damage: "matterPropertyDamageId",
  matter_settlement_counter: "matterSettlementCounterId",
  matter_settlement_demand: "matterSettlementDemandId",
  matter_settlement_offer: "matterSettlementOfferId",
  matter_task: "taskId",
  matter_trust_check: "matterTrustCheckId",
  matter_vehicle: "matterVehicleId",
  medical_treatment: "medicalTreatmentId",
  medical_treatment_appointment: "medicalTreatmentAppointmentId",
  medical_treatment_balance_confirmation:
    "medicalTreatmentBalanceConfirmationId",
  medical_treatment_bill: "medicalTreatmentBillId",
  medical_treatment_plan: "medicalTreatmentPlanId",
  medical_treatment_record: "medicalTreatmentRecordId",
};

const normalizeMatterEvent = (event: MatterEvent) => {
  const subjectField = event.primarySubjectType
    ? eventSubjectField[event.primarySubjectType]
    : undefined;
  const subject: LegacyEventSubject =
    subjectField && event.primarySubjectId
      ? { [subjectField]: event.primarySubjectId }
      : {};
  const currentPayload: Record<string, unknown> = {};
  const previousPayload: Record<string, unknown> = {};

  Object.entries(event.changes ?? {}).forEach(([field, change]) => {
    currentPayload[field] = change.to;
    previousPayload[field] = change.from;
  });

  return {
    ...event,
    ...subject,
    matterEventLogDataBeforeAction: JSON.stringify(previousPayload),
    matterEventLogDataCurrent: JSON.stringify(currentPayload),
    matterEventLogId: event.id,
    matterEventLogMessage: event.message ?? event.actionType ?? "",
    matterEventLogUsername: event.actorUserId ?? event.actor?.id ?? "",
  };
};
